import { Icon } from "@mirage-web/icons";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { PermissionGuard } from "~/components/permission-guard";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { cn } from "~/lib/utils";
import { createEmployeeMutation } from "~/modules/employee/mutations";
import type {
  CreateEmployeeRequest,
  CreateEmployeeResponse,
  OnboardingStepStatus,
} from "~/modules/employee/schemas";

export const Route = createFileRoute("/_dash/employee")({
  component: RouteComponent,
});

const TEAM_OPTIONS = [
  { value: "ops", label: "Ops (Admin, Finance, HR, Mgmt, etc.)", role: "ops" },
  { value: "marketing", label: "Marketing", role: "marketing" },
  { value: "design", label: "Design", role: "design" },
  {
    value: "motion-design-contractors",
    label: "Motion Designer (Contractor)",
    role: "motion-design-contractors",
  },
  { value: "cx-agent-contractors", label: "CX Agent (Contractor)", role: "cx-agent" },
  { value: "pm", label: "Product Management", role: "product" },
  { value: "eng", label: "Engineering/ML", role: "eng" },
  { value: "data-science", label: "Data Science", role: "data-science" },
  { value: "recruiting", label: "Recruiting", role: "recruiting" },
  { value: "contractors-interns", label: "Contractors / Interns", role: "contractor-default" },
] as const;

const STEP_LABELS: Record<string, string> = {
  create_user: "Create WorkOS & Captions account",
  add_statsig_segment: "Add to Statsig employees segment",
  add_oasis_org: "Add to Oasis organization",
};

const STEP_STATUS_CONFIG: Record<
  OnboardingStepStatus,
  {
    icon: "checkmark-fill" | "warning" | "close-filled";
    iconClass: string;
    badgeVariant: "default" | "outline" | "destructive";
    label: string;
  }
> = {
  completed: {
    icon: "checkmark-fill",
    iconClass: "text-green-600 dark:text-green-400",
    badgeVariant: "default",
    label: "Completed",
  },
  skipped: {
    icon: "warning",
    iconClass: "text-amber-600 dark:text-amber-400",
    badgeVariant: "outline",
    label: "Skipped",
  },
  failed: {
    icon: "close-filled",
    iconClass: "text-red-600 dark:text-red-400",
    badgeVariant: "destructive",
    label: "Failed",
  },
};

function getResultCardStyle(response: CreateEmployeeResponse) {
  const hasFailed = response.steps.some((s) => s.status === "failed");
  const hasSkipped = response.steps.some((s) => s.status === "skipped");

  if (hasFailed) {
    return {
      card: "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950",
      title: "text-red-900 dark:text-red-100",
      heading: "Employee Onboarding Completed With Errors",
    };
  }

  if (hasSkipped) {
    return {
      card: "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950",
      title: "text-amber-900 dark:text-amber-100",
      heading: "Employee Onboarding Completed",
    };
  }

  return {
    card: "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950",
    title: "text-green-900 dark:text-green-100",
    heading: "Employee Onboarded Successfully",
  };
}

function RouteComponent() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [team, setTeam] = useState<string>("");
  const [result, setResult] = useState<CreateEmployeeResponse | null>(null);

  const { mutate: createEmployee, isPending } = createEmployeeMutation.useMutation({
    onSuccess: (response) => {
      const hasFailed = response.steps.some((s) => s.status === "failed");
      const hasSkipped = response.steps.some((s) => s.status === "skipped");

      if (hasFailed) {
        toast.warning("Employee onboarding completed with errors");
      } else if (hasSkipped) {
        toast.info("Employee onboarding completed with skipped steps");
      } else {
        toast.success("Employee onboarded successfully");
      }

      setResult(response);
      setFirstName("");
      setLastName("");
      setUsername("");
      setTeam("");
    },
    onError: (error) => {
      toast.error("Failed to create employee");
      console.error("Employee creation error:", error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim() || !username.trim() || !team) {
      toast.error("All fields are required");
      return;
    }

    createEmployee({
      params: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        username: username.trim(),
        team: team as CreateEmployeeRequest["team"],
      },
    });
  };

  const selectedTeam = TEAM_OPTIONS.find((t) => t.value === team);

  return (
    <PermissionGuard permission="internal-account:write">
      <main className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Employee Onboarding</h1>
          <p className="text-muted-foreground mt-1">Create a new employee account</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>New Employee</CardTitle>
            <CardDescription>
              Enter employee details to create accounts in WorkOS and Captions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={isPending}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={isPending}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="username"
                    placeholder="jdoe"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isPending}
                    required
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    @nocapinc.com
                  </span>
                </div>
                {username && (
                  <p className="text-xs text-muted-foreground">
                    Email will be: {username}@nocapinc.com
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="team">Team *</Label>
                <Select value={team} onValueChange={setTeam} disabled={isPending}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEAM_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTeam && (
                  <p className="text-xs text-muted-foreground">
                    Oasis role will be: <span className="font-medium">{selectedTeam.role}</span>
                  </p>
                )}
              </div>

              <Button type="submit" disabled={isPending} className="w-full">
                {isPending ? "Creating Employee..." : "Create Employee"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Icon name="info" className="size-5" />
              What This Does
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                When you create an employee, the following actions are performed automatically:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>
                    <span className="font-medium">WorkOS & Captions account:</span> Creates user in
                    Firestore with both WorkOS IDs linked for Mirage Studio and Oasis access
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>
                    <span className="font-medium">Statsig segment:</span> Adds user to{" "}
                    <code className="text-xs bg-background px-1 py-0.5 rounded">employees</code>{" "}
                    segment
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>
                    <span className="font-medium">Oasis organization:</span> Adds user to internal
                    Oasis organization with role based on selected team
                  </span>
                </li>
              </ul>
              <p className="text-muted-foreground text-xs">
                Each step runs independently. If the user already exists, the remaining steps will
                still be attempted.
              </p>
            </div>
          </CardContent>
        </Card>

        {result && <ResultCard result={result} />}
      </main>
    </PermissionGuard>
  );
}

function ResultCard({ result }: { result: CreateEmployeeResponse }) {
  const style = getResultCardStyle(result);

  return (
    <Card className={style.card}>
      <CardHeader>
        <CardTitle className={style.title}>{style.heading}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div>
            <span className="font-medium">Name:</span> {result.name}
          </div>
          <div>
            <span className="font-medium">Email:</span> {result.email}
          </div>
          <div>
            <span className="font-medium">User ID:</span>{" "}
            <code className="text-xs bg-black/5 dark:bg-white/10 px-1 py-0.5 rounded">
              {result.userId}
            </code>
          </div>
        </div>

        <div className="border-t pt-3 space-y-2">
          <p className="text-sm font-medium">Steps</p>
          {result.steps.map((step) => {
            const config = STEP_STATUS_CONFIG[step.status];
            return (
              <div key={step.step} className="flex items-start gap-2 text-sm">
                <Icon
                  name={config.icon}
                  className={cn("size-4 mt-0.5 shrink-0", config.iconClass)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span>{STEP_LABELS[step.step] ?? step.step}</span>
                    <Badge variant={config.badgeVariant}>{config.label}</Badge>
                  </div>
                  {step.message && (
                    <p className="text-xs text-muted-foreground mt-0.5">{step.message}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
